import { createFileRoute, Link, Outlet, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo, type ComponentType } from "react";
import {
  LogOut, Calendar, User, MessageCircle, ShieldCheck, FolderLock, KeyRound,
  GraduationCap, Newspaper, Briefcase, FileText, Mic,
  FileSignature, Award, ChevronDown, LayoutDashboard, Sparkles, Library,
  Radio, Megaphone, Mail, Scale, BookOpen, Receipt, CreditCard,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getMyProfile } from "@/lib/api/portal.functions";
import { getMySubscription } from "@/lib/api/subscriptions.functions";
import { TrialBanner } from "@/components/site/TrialBanner";
import { AdminRealtimeAlerts } from "@/components/site/AdminRealtimeAlerts";
import { adminGroups, type AdminNavGroup as NavGroup } from "@/lib/admin-nav";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/portal")({
  component: PortalLayout,
});

type NavItem = { to: string; label: string; icon: ComponentType<{ className?: string }>; exact?: boolean };

const clientGroups: NavGroup[] = [
  {
    id: "dashboard",
    label: "Start here",
    items: [
      { to: "/portal/dashboard", label: "My dashboard (everything)", icon: LayoutDashboard },
      { to: "/portal", label: "My bookings", icon: Calendar, exact: true },
      { to: "/portal/documents", label: "My files & downloads", icon: FolderLock },
    ],
  },
  {
    id: "learn",
    label: "Learn",
    items: [
      { to: "/portal/academy", label: "NDLCS Academy (free)", icon: Sparkles },
      { to: "/portal/certificates", label: "My certificates", icon: Award },
    ],
  },
  {
    id: "student",
    label: "Student portal",
    items: [
      { to: "/portal/student", label: "Student dashboard", icon: GraduationCap, exact: true },
      { to: "/portal/student/workspace", label: "Academic workspace", icon: FolderLock },
      { to: "/portal/student/assignments", label: "Assignment centre", icon: FileText },
      { to: "/portal/student/portfolio", label: "My portfolio", icon: Award },
      { to: "/portal/student/profile", label: "Student profile", icon: User },
    ],
  },
  {
    id: "ai-tools",
    label: "AI legal tools",
    items: [
      { to: "/portal/tools-dashboard", label: "Tools & credits", icon: LayoutDashboard },
      { to: "/portal/labour-analyzer", label: "AI Labour-Law Analyzer", icon: Scale },
    ],
  },
  {
    id: "education",
    label: "Education",
    items: [
      { to: "/portal/llb-tutoring", label: "UNISA LLB tutoring", icon: GraduationCap },
      { to: "/portal/llb-receipts", label: "LLB receipts & audit", icon: Receipt },
    ],
  },

  {
    id: "career",
    label: "Career",
    items: [
      { to: "/portal/cv", label: "AI CV Builder", icon: FileText },
      { to: "/portal/job-matches", label: "My job matches", icon: Briefcase },
      { to: "/portal/interview", label: "Interview Coach", icon: Mic },
    ],
  },
  {
    id: "account",
    label: "My Account",
    items: [
      { to: "/portal/dashboard", label: "My dashboard", icon: User },
      { to: "/portal/account", label: "Account summary", icon: User },
      { to: "/portal/documents", label: "My documents", icon: FolderLock },
      { to: "/portal/matters", label: "My matters", icon: FolderLock },

      // Company assets are confidential internal records — admin-only.
      { to: "/portal/subscription", label: "Subscription", icon: CreditCard },
      { to: "/portal/orders", label: "Order tracker", icon: Receipt },
      { to: "/portal/my-payments", label: "My payments", icon: Receipt },
      { to: "/portal/credits", label: "My credits", icon: FileSignature },
      { to: "/portal/profile", label: "Profile", icon: User },
      { to: "/portal/chat", label: "Live chat", icon: MessageCircle },
    ],
  },
];


function NavSection({ group, pathname }: { group: NavGroup; pathname: string }) {
  const containsActive = useMemo(
    () => group.items.some((i) => (i.exact ? pathname === i.to : pathname.startsWith(i.to))),
    [group, pathname],
  );
  const [open, setOpen] = useState(containsActive || group.id === "dashboard" || group.id === "learn" || group.id === "admin-operations");

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-navy"
      >
        <span>{group.label}</span>
        <ChevronDown className={cn("h-3 w-3 transition-transform", open ? "rotate-0" : "-rotate-90")} />
      </button>
      {open && (
        <div className="mt-1 space-y-0.5">
          {group.items.map((i) => (
            <Link
              key={`${i.to}-${i.label}`}
              to={i.to as any}
              search={i.search as any}
              activeOptions={{ exact: !!i.exact }}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/80 transition-colors hover:bg-muted"
              activeProps={{ className: "bg-navy text-navy-foreground hover:bg-navy" }}
            >
              <i.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{i.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PortalLayout() {
  const fetchProfile = useServerFn(getMyProfile);
  const fetchSub = useServerFn(getMySubscription);
  const { data } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });
  const { data: subData } = useQuery({
    queryKey: ["my-sub"],
    queryFn: () => fetchSub(),
    staleTime: 60_000,
  });
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    await router.invalidate();
    navigate({ to: "/" });
  }

  const isAdmin = data?.isAdmin ?? false;
  const name = data?.profile?.full_name || data?.profile?.email || "Client";

  return (
    <div className="container-narrow py-10">
      {isAdmin && <AdminRealtimeAlerts />}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2">
            {isAdmin ? "Admin console" : "Client portal"}
            {isAdmin && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-gold ring-1 ring-gold/40">
                <ShieldCheck className="h-3 w-3" /> ADMIN
              </span>
            )}
          </p>
          <h1 className="mt-1 font-display text-3xl text-navy">
            Hi {String(name).split(" ")[0]}
            {isAdmin && (
              <span className="ml-2 align-middle text-xs font-medium text-muted-foreground">
                · signed in as administrator
              </span>
            )}
          </h1>
        </div>

        <button
          onClick={signOut}
          className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:border-navy"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-[240px_1fr]">
        <nav className="md:sticky md:top-6 md:self-start rounded-xl border border-border bg-card p-3 shadow-soft">
          <div className="mb-2 flex items-center gap-2 px-3 py-2 text-sm font-semibold text-navy">
            <LayoutDashboard className="h-4 w-4 text-gold" />
            <span>My workspace</span>
          </div>
          {clientGroups.map((g) => (
            <NavSection key={g.id} group={g} pathname={pathname} />
          ))}

          {isAdmin && (
            <>
              <div className="my-3 border-t border-border" />
              <div className="mb-2 flex items-center gap-2 px-3 py-2 text-sm font-semibold text-navy">
                <ShieldCheck className="h-4 w-4 text-gold" />
                <span>Admin</span>
              </div>
              {adminGroups.map((g) => (
                <NavSection key={g.id} group={g} pathname={pathname} />
              ))}
            </>
          )}
        </nav>

        <div className="min-w-0">
          {!isAdmin && subData && (
            <TrialBanner
              trialEndsAt={subData.trial?.endsAt ?? null}
              trialActive={!!subData.trial?.active}
              subscriptionActive={!!subData.subscriptionActive}
            />
          )}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
